import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

/**
 * TimePicker component with separate hour and minute dropdowns
 * @param {string} value - Time value in "HH:MM" format
 * @param {function} onChange - Callback when time changes
 * @param {string} placeholder - Placeholder text
 * @param {string} className - Additional CSS classes
 * @param {number} minHour - Minimum hour (default: 0)
 * @param {number} maxHour - Maximum hour (default: 23)
 * @param {number} minuteStep - Minute interval (default: 5)
 * @param {boolean} disabled - Disable the picker
 */
export default function TimePicker({ 
  value, 
  onChange, 
  placeholder = "Select time",
  className = "",
  minHour = 8,
  maxHour = 22,
  minuteStep = 5,
  disabled = false,
  dataTestId = ""
}) {
  // Parse current value
  const [hour, minute] = value ? value.split(':').map(Number) : ['', ''];
  
  // Generate hours array
  const hours = [];
  for (let h = minHour; h <= maxHour; h++) {
    hours.push(h);
  }
  
  // Generate minutes array (0, 5, 10, 15, ..., 55)
  const minutes = [];
  for (let m = 0; m < 60; m += minuteStep) {
    minutes.push(m);
  }

  const handleHourChange = (newHour) => {
    const h = parseInt(newHour);
    const m = minute !== '' && !isNaN(minute) ? minute : 0;
    onChange(`${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`);
  };

  const handleMinuteChange = (newMinute) => {
    const h = hour !== '' && !isNaN(hour) ? hour : minHour;
    const m = parseInt(newMinute);
    onChange(`${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`);
  };

  return (
    <div className={`flex gap-2 items-center ${className}`}>
      {/* Hour Selector */}
      <Select 
        value={hour !== '' && !isNaN(hour) ? hour.toString() : ''} 
        onValueChange={handleHourChange}
        disabled={disabled}
      >
        <SelectTrigger 
          className="w-20 rounded-xl" 
          data-testid={dataTestId ? `${dataTestId}-hour` : undefined}
        >
          <SelectValue placeholder="Saat" />
        </SelectTrigger>
        <SelectContent>
          {hours.map((h) => (
            <SelectItem key={h} value={h.toString()}>
              {h.toString().padStart(2, '0')}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      
      <span className="text-grey font-medium">:</span>
      
      {/* Minute Selector */}
      <Select 
        value={minute !== '' && !isNaN(minute) ? minute.toString() : ''} 
        onValueChange={handleMinuteChange}
        disabled={disabled}
      >
        <SelectTrigger 
          className="w-20 rounded-xl"
          data-testid={dataTestId ? `${dataTestId}-minute` : undefined}
        >
          <SelectValue placeholder="Dk" />
        </SelectTrigger>
        <SelectContent>
          {minutes.map((m) => (
            <SelectItem key={m} value={m.toString()}>
              {m.toString().padStart(2, '0')}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}
